import { DecisionGroupCircles } from "@/components/shared";
import { DecisionGroup } from "@/components/shared/DecisionGroupCircles";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { PendingMarginDecisionGroup, FetchPendingMarginDecisionGroupsQuery, FetchPendingMarginDecisionGroupsQueryVariables, FetchPendingMarginDecisionGroupsDocument } from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";
import useUserStore from "@/store/shield";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

function PendingMarginDecisions() {

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const navigate = useNavigate();
  const { data, loading } = useQuery<FetchPendingMarginDecisionGroupsQuery, FetchPendingMarginDecisionGroupsQueryVariables>(FetchPendingMarginDecisionGroupsDocument, {
    fetchPolicy: "cache-and-network",
  });

  const marginDecisionGroups = data?.fetchPendingMarginDecisionGroups ?? [];
  const organizationId = useUserStore((state) => state.orgId);

  const onClickHandler = (marginDecisionGroup: PendingMarginDecisionGroup) => {
    if (!marginDecisionGroup) return;

    // Default to first group in the list
    navigate(`/tender/${marginDecisionGroup.tenderId}/margindecisions/${marginDecisionGroup.groups[0].id}`);
  };

  // Handle navigation when a specific group circle is clicked
  const onGroupClickHandler = (marginDecisionGroup: PendingMarginDecisionGroup, group: DecisionGroup) => {
    if (!marginDecisionGroup || !group) return;

    // Find the matching group in the marginDecisionGroup.groups array
    const matchingGroup = marginDecisionGroup.groups.find(
      (g: any) => g.operationalOrganizationId === group.operationalOrganizationId
    );

    if (matchingGroup) {
      // Navigate to the specific group's margin decision page
      navigate(`/tender/${marginDecisionGroup.tenderId}/margindecisions/${matchingGroup.id}?openAccordion=item-2`);
    }
  };

  /**
   * Checks if any group within the margin decision group is awaiting the organization.
   *
   * @param {Partial<PendingMarginDecisionGroup>} marginDecisionGroup - The margin decision group to check.
   * @returns {boolean} - Returns true if any group is awaiting the organization, otherwise false.
   */
  const isAwaitingOrg = (marginDecisionGroup: Partial<PendingMarginDecisionGroup>): boolean => {
    if (!marginDecisionGroup.groups) return false;

    return marginDecisionGroup.groups.find((group: any) => group.awaitingOrganizationId === organizationId)
      ? true
      : false;
  };

  /**
   * Sort margin decision groups by whether it is the user's turn to margin.
   */
  const sortedMarginDecisionGroups = [...marginDecisionGroups].sort((a, b) => {
    const isAwaitingOrgA = isAwaitingOrg(a as Partial<PendingMarginDecisionGroup>) ? 0 : 1;
    const isAwaitingOrgB = isAwaitingOrg(b as Partial<PendingMarginDecisionGroup>) ? 0 : 1;
    return isAwaitingOrgA - isAwaitingOrgB;
  });

  return (
    <Card data-cy="dashboard-margin-decision-container" className="grow flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="pl-2 text-base flex justify-between items-center">
          {t("pendingmargindecisions.title")}
          <HoverCard>
            <HoverCardTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 p-0 hover:cursor-help"
              >
                <InformationCircleIcon className="h-5 w-5" />
              </Button>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 text-sm font-normal">
              {t("pendingmargindecisions.tooltip")}
            </HoverCardContent>
          </HoverCard>
        </CardTitle>
      </CardHeader>
      <CardContent className="h-full pr-3">
        <div className="flex flex-col gap-2 h-full" data-cy="dashboard-margin-decision-content-wrapper">
          {(() => {
            if (loading) {
              // Render Loading Skeletons
              return Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={`margin-decision-loading-skeleton-${index}`}
                  className="flex gap-4 items-center w-full px-2"
                  aria-hidden="true"
                >
                  <div className="flex grow flex-col gap-2">
                    <Skeleton className="w-1/3 h-4" />
                    <Skeleton className="w-2/3 h-2" />
                  </div>
                  <Skeleton className="w-14 h-8" />
                </div>
              ));
            }

            if (!loading && (marginDecisionGroups.length === 0 || !marginDecisionGroups)) {
              // Render Empty State
              return (
                <div
                  className="text-sm text-gray-500"
                  data-cy="dashboard-margin-decision-content-empty"
                >
                  {t("pendingmargindecisions.nomargindecisions")}
                </div>
              );
            }

            // Render List of Margin Decision Groups
            return (
              // Height calculated based on height of header, card content, card title, and card header and assumed pending margin decision component
              <ScrollArea className="flex flex-col gap-2 max-h-[300px] lg:max-h-none lg:h-[calc(50vh-8.5rem)] pr-3">
                {sortedMarginDecisionGroups.map((marginDecisionGroup) => {
                  const isMyTurn = isAwaitingOrg(marginDecisionGroup as Partial<PendingMarginDecisionGroup>);

                  return (
                    <div key={`margin-decision-group-${marginDecisionGroup?.tenderId}`}>
                      <div
                        className="hover:bg-gray-100 rounded-md py-1 px-2 cursor-pointer"
                        onClick={() => onClickHandler(marginDecisionGroup)}
                      >
                        <div
                          className="flex gap-4 items-center text-sm"
                          data-cy="dashboard-margin-decision-content-item"
                        >
                          <div className="flex grow flex-col min-w-0">
                            <div className="line-clamp-2">{marginDecisionGroup.name}</div>
                            <div className={cn("text-xs", isMyTurn ? "text-orange-500" : "text-gray-500")}>
                              {isMyTurn
                                ? t("pendingmargindecisions.awaitingresponse")
                                : t("pendingmargindecisions.awaitingotherresponse")}
                            </div>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <DecisionGroupCircles
                              groups={marginDecisionGroup.groups as DecisionGroup[]}
                              onGroupClick={(group) => onGroupClickHandler(marginDecisionGroup, group)}
                            />
                          </div>
                        </div>
                      </div>
                      <Separator className="my-1" />
                    </div>
                  );
                })}
              </ScrollArea>
            );
          })()}
        </div>

      </CardContent>
    </Card>
  );
}

export default PendingMarginDecisions;
